import { isoDateTime, z } from "../zod.js";
import { Locale, SUPPORTED_LOCALES } from "../locales.js";
import { Uuid } from "../identity/admin-schemas.js";

// Module 17 — Messaging: the contract DTOs for slice P3-B (spec 17
// §2.1–§2.13, §3.1–§3.4, §3.7, §4.1–§4.5 — adjudicated P3-0, D-231/D-232).
// Part 1: provider accounts + health, identity linking (code handshake),
// templates + variants, the channel-abstracted send, preferences, and the
// public Telegram webhook. Part 2: the conversation-inbox rows (§5:
// conversations list/thread head/thread messages, the office reply, PATCH +
// read, media upload-url, manual retry, message detail) and the §4.4
// webhook-event log tail. P3-E2 PR E2-2e (D-327): email intake, inbound
// only, one monitored address per tenant; the `email` provider on the
// response side and the read filters, `ChatProvider` on every request, the
// email message's subject, marks and parts, the intake switch, the
// Unverified mail list and its acts, and the held-part download.
//
// WHAT IS ABSENT, AND WHY THAT IS THE POINT (the statement every module's
// first slice wrote — D-026/D-033):
//   * The Meta template rows (§5: variant submit, templates/sync) are PARKED
//     with WhatsApp (D-232): their action calls a BSP that does not exist.
//     They land at unparking, on the owner's BSP DECISIONS entry.
//   * The WA webhook row (GET+POST /v1/webhooks/messaging/whatsapp/…) is
//     parked with them: its GET challenge is the connect flow's round-trip,
//     and connect answers 422 WA_PARKED. Integration tests drive the dormant
//     WA machinery through the per-environment stub adapter against seeded
//     rows, not through a webhook route nothing can subscribe.
//   * `wa_signup_payload` on connect is likewise absent until unparking —
//     a request field only a parked flow could send is a stub, one level
//     down. The `whatsapp` provider VALUE is admitted (the dormant machinery
//     is built to shape) and answers the typed refusal.
//
// CONVENTIONS carried from inventory/waste/production/quality/sales:
//   * REDACTION IS OMISSION (§7): `address` fields render masked (not
//     omitted) for non-holders of `messaging.address.read` — masking IS the
//     §7-stated form for addresses, unlike money omission; `secret_arn`,
//     `telegram_secret_token`, `webhook_token` are contract-level omits and
//     appear on NO schema below.
//   * Lists answer `{items, next_cursor}` envelopes.
//   * D-225: no server-minted prose — refusals are the problem catalog at
//     the end; degradations are typed fields; template bodies/bot replies
//     are AUTHORED four-locale artifacts living in template variants and the
//     command reply tables, never composed server-side.
//   * D-142: undeclared query parameters are refused; every list declares
//     its filter set exactly.

// --- closed vocabularies (spec 17 §2) ---------------------------------------

/** Every provider a channel account, an identity, a conversation or a
 * message can name (spec 17 §2.1, §2.2, §2.6, §2.7). `email` joined with
 * P3-E2 PR E2-2e (D-327 ruling 19), on the RESPONSE side, the event shapes
 * and the read filters only: the tenant's email account is the intake switch
 * (§2.1), which no request names a provider to reach. */
export const MessagingProvider = z.enum(["telegram", "whatsapp", "email"]);
/** The providers a request may name to connect, link or route through, and
 * the shapes that can only ever name one of them (D-327 ruling 19). It keeps
 * every request exactly as it accepted before email existed, so a connect, a
 * link code, a preference or a variant naming `email` is refused 400
 * VALIDATION_FAILED by the schema, and no request accepts more than it did.
 * A template variant, a channel preference, the channel-abstracted send's
 * answer and a webhook log row keep it on the response side too, because
 * none of them can name email (§2.5, §2.10 and §2.12 keep the two chat
 * providers, and §3.3 step 1's walk reads chat identities alone). */
export const ChatProvider = z.enum(["telegram", "whatsapp"]);
export const ChannelAccountStatus = z.enum([
  "connecting",
  "active",
  "webhook_failing",
  "suspended",
  "disconnected",
]);
export const MessagingOwnerKind = z.enum(["customer_contact", "user"]);
export const ChannelIdentityStatus = z.enum(["pending", "verified", "revoked"]);
export const MessagingLocale = Locale;
export const TemplateCategory = z.enum(["utility", "marketing", "authentication"]);
export const TemplateMetaStatus = z.enum([
  "not_required",
  "draft",
  "submitted",
  "approved",
  "rejected",
  "paused",
  "disabled",
]);
export const MetaQualityScore = z.enum(["green", "yellow", "red"]);
export const WaVerificationStatus = z.enum(["pending", "verified", "rejected"]);
export const WaMessagingTier = z.enum(["tier_1k", "tier_10k", "tier_100k", "unlimited"]);
export const MessageStatus = z.enum([
  "received",
  "queued",
  "sending",
  "sent",
  "delivered",
  "read",
  "failed_retryable",
  "failed_permanent",
  "suppressed",
]);
export const MessagePriority = z.enum(["transactional", "bulk"]);
export const ProviderHealthState = z.enum(["healthy", "degraded", "down"]);

// --- provider accounts (§2.1, §2.13; §3.1) ----------------------------------

/** §2.13 circuit-breaker state, embedded on account reads and the health
 * snapshot. `window_error_rate` is numeric(5,4) as exact decimal text
 * (D-041). */
export const ProviderHealth = z
  .object({
    state: ProviderHealthState,
    consecutive_failures: z.number().int().min(0),
    window_error_rate: z.string().regex(/^(0(\.\d{1,4})?|1(\.0{1,4})?)$/),
    opened_at: isoDateTime({ offset: true }).nullable(),
    next_probe_at: isoDateTime({ offset: true }).nullable(),
    last_success_at: isoDateTime({ offset: true }).nullable(),
    last_failure_at: isoDateTime({ offset: true }).nullable(),
    last_failure_detail: z.string().nullable(),
  })
  .openapi("MessagingProviderHealth");

/** §2.1 — `secret_arn`, `webhook_token` and `telegram_secret_token` are
 * NEVER serialized (§7 contract-level omit): this schema simply has no such
 * keys, which is the whole enforcement at the contract layer.
 *
 * The tenant's `email` account (P3-E2 PR E2-2e, D-327 rulings 5 and 19) is
 * served in this same shape: `display_name` is the intake address, the
 * Telegram and WhatsApp columns are null, `status` is `active` (intake on) or
 * `disconnected` (intake off), `last_webhook_at` is when the tenant's newest
 * mail arrived, and `health` is the receiving rule set's state as this
 * tenant last probed or received it (§2.13). Enabling intake writes that
 * health row, so it is never null on an email account. */
export const ChannelAccount = z
  .object({
    id: Uuid,
    provider: MessagingProvider,
    display_name: z.string(),
    status: ChannelAccountStatus,
    telegram_bot_username: z.string().nullable(),
    wa_business_account_id: z.string().nullable(),
    wa_phone_number_id: z.string().nullable(),
    wa_phone_e164: z.string().nullable(),
    wa_verification_status: WaVerificationStatus.nullable(),
    wa_messaging_tier: WaMessagingTier.nullable(),
    default_locale: MessagingLocale,
    connected_by: Uuid,
    connected_at: isoDateTime({ offset: true }).nullable(),
    last_webhook_at: isoDateTime({ offset: true }).nullable(),
    health: ProviderHealth.nullable().openapi({
      description:
        "§2.13 row for this account; null only before the connect transaction commits it. An `email` account's row is written when intake is enabled, so it is never null there (D-327 ruling 19)",
    }),
    created_at: isoDateTime({ offset: true }),
  })
  .openapi("ChannelAccount");

/** §3.1 connect. Telegram is the live path (D-002); `provider='whatsapp'`
 * answers 422 WA_PARKED while D-232 stands — the wa_signup_payload field
 * joins this schema at unparking, with the flow that can send it. */
export const ChannelAccountConnectRequest = z
  .object({
    provider: ChatProvider,
    /** The @BotFather token. Required when provider='telegram'
     * (service-enforced); validated via getMe, stored ONLY in SSM. */
    telegram_bot_token: z.string().min(20).max(200).optional(),
  })
  .strict()
  .openapi("ChannelAccountConnectRequest");

export const ChannelAccountListResponse = z
  .object({ items: z.array(ChannelAccount), next_cursor: z.string().nullable() })
  .openapi("ChannelAccountListResponse");

export const ChannelAccountDisconnectResponse = z
  .object({ id: Uuid, status: z.literal("disconnected") })
  .openapi("ChannelAccountDisconnectResponse");

/** §5 probe — re-runs getWebhookInfo/setWebhook through the adapter and
 * reports what the provider said; a typed result, never prose (D-225). On an
 * `email` account (D-327 ruling 19) it re-reads the active receiving rule
 * set instead: `webhook_ok` is true exactly when ours is active, and
 * `provider_detail` is `ruleset_inactive` or `ruleset_check_failed`
 * otherwise, never a rule set's name. */
export const ChannelAccountProbeResponse = z
  .object({
    id: Uuid,
    status: ChannelAccountStatus,
    webhook_ok: z.boolean(),
    provider_detail: z.string().nullable().openapi({
      description: "Provider's own last-error text, verbatim (§4.4 renders it as-is)",
    }),
    last_webhook_at: isoDateTime({ offset: true }).nullable(),
  })
  .openapi("ChannelAccountProbeResponse");

/** GET /v1/messaging/health — §5's per-account snapshot for the settings
 * strip: §2.13 state + queue posture. An `email` account is served in the
 * same item (D-327 ruling 19) with `queued_count` 0 and `oldest_queued_at`
 * null, because nothing is ever sent through it. */
export const MessagingHealthSnapshot = z
  .object({
    channel_account_id: Uuid,
    provider: MessagingProvider,
    account_status: ChannelAccountStatus,
    health: ProviderHealth,
    queued_count: z.number().int().min(0),
    oldest_queued_at: isoDateTime({ offset: true }).nullable(),
  })
  .openapi("MessagingHealthSnapshot");

export const MessagingHealthResponse = z
  .object({ items: z.array(MessagingHealthSnapshot) })
  .openapi("MessagingHealthResponse");

// --- identities + linking (§2.2, §2.3; §3.2) --------------------------------

/** §2.2 — `address` is the full value ONLY for holders of the
 * `messaging.address.read` field grant; for everyone else (and every §8
 * agent tool output) the serializer masks it (`+7•••••1234`, `chat:•••482`)
 * — masking, not omission, is §7's stated form for addresses. */
export const ChannelIdentity = z
  .object({
    id: Uuid,
    channel_account_id: Uuid,
    provider: MessagingProvider,
    owner_kind: MessagingOwnerKind,
    owner_id: Uuid,
    address: z.string().openapi({
      description:
        "Masked unless the caller holds messaging.address.read (spec 17 §7 field redaction)",
    }),
    provider_profile_name: z.string().nullable().openapi({
      description:
        "The provider's profile name. On an `email` identity it is the From display name, which is address-class data (D-327 ruling 20): it is served only to a caller holding messaging.address.read and is null for every other caller",
    }),
    language: MessagingLocale,
    status: ChannelIdentityStatus,
    opted_out: z.boolean(),
    opted_out_hard: z.boolean(),
    verified_at: isoDateTime({ offset: true }).nullable(),
    revoked_at: isoDateTime({ offset: true }).nullable(),
    revoked_reason: z.string().nullable(),
    created_at: isoDateTime({ offset: true }),
  })
  .openapi("ChannelIdentity");

export const ChannelIdentityListResponse = z
  .object({ items: z.array(ChannelIdentity), next_cursor: z.string().nullable() })
  .openapi("ChannelIdentityListResponse");

/** §3.2.1 issue. Owner narrowing (customer_contact → sales/fa/ga) is
 * service-side on `messaging.link.issue` (D-040 two-layer). */
export const LinkCodeIssueRequest = z
  .object({
    owner_kind: MessagingOwnerKind,
    owner_id: Uuid,
    provider: ChatProvider,
  })
  .strict()
  .openapi("LinkCodeIssueRequest");

/** The plaintext code is returned ONCE and never stored (§2.3 — SHA-256
 * only). `deep_link` is Telegram's t.me/{bot}?start=link_{code}; null for
 * WhatsApp (the customer texts `LINK {code}`). */
export const LinkCodeIssueResponse = z
  .object({
    verification_id: Uuid,
    code: z.string().regex(/^[A-HJ-NP-Z2-9]{6}$/),
    deep_link: z.url().nullable(),
    expires_at: isoDateTime({ offset: true }),
  })
  .openapi("LinkCodeIssueResponse");

export const IdentityRevokeRequest = z
  .object({ reason: z.string().min(1).max(400) })
  .strict()
  .openapi("IdentityRevokeRequest");

/** PATCH — language and the SOFT opt-out only. `opted_out_hard` has no key
 * here by design: §9.15 — staff cannot clear it; only the person re-opts-in
 * by messaging START. */
export const IdentityUpdateRequest = z
  .object({
    language: MessagingLocale.optional(),
    opted_out: z.boolean().optional(),
  })
  .strict()
  .openapi("IdentityUpdateRequest");

// --- templates (§2.4, §2.5) -------------------------------------------------

/** §2.4 `variables` element — ordered; samples mandatory (Meta submission
 * requires example values, and previews render from them). */
export const MessagingTemplateVariable = z
  .object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    sample: z.string().min(1).max(200),
    required: z.boolean(),
  })
  .strict()
  .openapi("MessagingTemplateVariable");

/** §2.5 buttons element (Telegram inline keyboard / WA template buttons). */
export const MessagingTemplateButton = z
  .object({
    kind: z.enum(["quick_reply", "url"]),
    label: z.string().min(1).max(64),
    url: z.string().max(1000).optional(),
  })
  .strict()
  .openapi("MessagingTemplateButton");

export const MessageTemplateVariant = z
  .object({
    id: Uuid,
    provider: ChatProvider,
    locale: MessagingLocale,
    body: z.string(),
    header_text: z.string().nullable(),
    footer_text: z.string().nullable(),
    buttons: z.array(MessagingTemplateButton),
    meta_status: TemplateMetaStatus,
    meta_template_name: z.string().nullable(),
    meta_rejected_reason: z.string().nullable(),
    meta_quality_score: MetaQualityScore.nullable(),
    submitted_at: isoDateTime({ offset: true }).nullable(),
    decided_at: isoDateTime({ offset: true }).nullable(),
    updated_at: isoDateTime({ offset: true }),
  })
  .openapi("MessageTemplateVariant");

export const MessageTemplate = z
  .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    category: TemplateCategory,
    variables: z.array(MessagingTemplateVariable),
    is_system: z.boolean(),
    is_active: z.boolean(),
    variants: z.array(MessageTemplateVariant),
    created_at: isoDateTime({ offset: true }),
    updated_at: isoDateTime({ offset: true }),
  })
  .openapi("MessageTemplate");

export const MessageTemplateListResponse = z
  .object({ items: z.array(MessageTemplate), next_cursor: z.string().nullable() })
  .openapi("MessageTemplateListResponse");

export const MessagingTemplateCreateRequest = z
  .object({
    code: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    name: z.string().min(1).max(200),
    category: TemplateCategory,
    variables: z.array(MessagingTemplateVariable).max(20).default([]),
  })
  .strict()
  .openapi("MessagingTemplateCreateRequest");

export const MessagingTemplateUpdateRequest = z
  .object({
    name: z.string().min(1).max(200).optional(),
    variables: z.array(MessagingTemplateVariable).max(20).optional(),
    /** false on a system template → 422 SYSTEM_TEMPLATE_PROTECTED (the
     * order-status pipeline reads them; §2.4 — undeletable, only editable). */
    is_active: z.boolean().optional(),
  })
  .strict()
  .openapi("MessagingTemplateUpdateRequest");

/** PUT variant body (§4.3 editor limits, service-enforced against provider
 * caps): upserting a WA variant resets its meta_status to `draft` (§5) —
 * dormant bookkeeping while D-232 stands, exercised via the stub adapter.
 * A TELEGRAM body is also held to §2.5's HTML subset by the service (task
 * #238, `apps/api/src/modules/messaging/templates/telegram-html.ts`); zod
 * cannot express that grammar, so the refusal is the service's 422
 * VALIDATION_FAILED, described on the route in register.ts. */
/**
 * How many buttons a variant may carry, per provider (D-593 ruling 5). A
 * WhatsApp template allows three quick replies. A Telegram variant draws one
 * inline button per row, and the language picker alone needs one per product
 * locale, so its cap is the registry's locale count with room for four more
 * (ten today). The body schema below admits the larger of the two; the
 * service refuses a WhatsApp variant over its three by name.
 */
export const TEMPLATE_BUTTON_CAPS = {
  telegram: SUPPORTED_LOCALES.length + 4,
  whatsapp: 3,
} as const satisfies Record<"telegram" | "whatsapp", number>;

export const TemplateVariantPutRequest = z
  .object({
    body: z.string().min(1).max(1024),
    header_text: z.string().max(60).optional(),
    footer_text: z.string().max(60).optional(),
    buttons: z
      .array(MessagingTemplateButton)
      .max(Math.max(TEMPLATE_BUTTON_CAPS.telegram, TEMPLATE_BUTTON_CAPS.whatsapp))
      .default([]),
  })
  .strict()
  .openapi("TemplateVariantPutRequest");

// --- channel-abstracted send (§3.3; §5 /messages/send) ----------------------

/** §3.3.1's entry for module 12 (P4), agents and service-to-service callers.
 * Template-only on this route: free-text/media sends are the conversation
 * reply's business (POST /v1/messaging/conversations/{id}/messages).
 * `notification_id`/`approval_request_id` are the
 * module-12-facing columns landing dormant (charter stated absences/D-231):
 * accepted, persisted, and NULL in practice until 12 exists. */
export const MessageSendRequest = z
  .object({
    owner_kind: MessagingOwnerKind,
    owner_id: Uuid,
    template_code: z.string().min(1).max(64),
    template_vars: z.record(z.string(), z.string().max(1000)).default({}),
    priority: MessagePriority.default("transactional"),
    notification_id: Uuid.optional(),
    approval_request_id: Uuid.optional(),
  })
  .strict()
  .openapi("MessageSendRequest");

/** 201 union: `queued` (dispatch pending) or `suppressed` (a §3.3.2 policy
 * gate persisted the refusal — `status_detail` says which, as a typed code
 * string, never prose). The no-channel case is a 409 NO_CHANNEL problem:
 * nothing was created, and the caller (module 12, P4) falls back to email. */
export const MessageSendResponse = z
  .object({
    message_id: Uuid,
    conversation_id: Uuid,
    provider: ChatProvider,
    status: z.enum(["queued", "suppressed"]),
    status_detail: z.string().nullable(),
  })
  .openapi("MessageSendResponse");

// --- preferences (§2.12) ----------------------------------------------------

export const ChannelPreference = z
  .object({
    owner_kind: MessagingOwnerKind,
    owner_id: Uuid,
    primary_provider: ChatProvider,
    fallback_provider: ChatProvider.nullable(),
    fallback_after_minutes: z.number().int().min(1).max(1440),
    /** false = no row exists; the column DEFAULTS above are what resolution
     * uses (§3.3.1) — a typed fact, not an invented row. */
    explicit: z.boolean(),
  })
  .openapi("ChannelPreference");

export const ChannelPreferencePutRequest = z
  .object({
    primary_provider: ChatProvider,
    fallback_provider: ChatProvider.nullable().default(null),
    fallback_after_minutes: z.number().int().min(1).max(1440).default(15),
  })
  .strict()
  .refine((v) => v.fallback_provider === null || v.fallback_provider !== v.primary_provider, {
    message: "fallback_provider must differ from primary_provider",
  })
  .openapi("ChannelPreferencePutRequest");

// --- Telegram webhook (§3.4; public) ----------------------------------------

/** The raw Telegram Update. Deliberately NON-strict (the one place D-142's
 * refusal does not apply — the body is PROVIDER-shaped and Telegram adds
 * fields at will): `update_id` is the §2.10 dedup key and everything else
 * lands verbatim in `webhook_events.payload` before parsing (§3.4.2). */
export const TelegramWebhookUpdate = z
  .object({ update_id: z.number().int() })
  .loose()
  .openapi("TelegramWebhookUpdate");

export const TelegramWebhookAck = z.object({ ok: z.literal(true) }).openapi("TelegramWebhookAck");

// --- conversations (§2.6/§2.7/§2.8/§2.9; §4.1/§4.2; §5 — PART 2) ------------

export const ConversationKind = z.enum(["customer", "staff"]);
export const ConversationStatus = z.enum(["open", "snoozed", "closed"]);
export const MessageDirection = z.enum(["inbound", "outbound"]);
export const MessageSenderKind = z.enum(["contact", "user", "agent", "system"]);
export const MessageContentKind = z.enum([
  "text",
  "template",
  "media",
  "interactive_reply",
  "command",
  "location",
  "unsupported",
  /** Every email message (D-327 ruling 10). Email never enters the command
   * router, so its content kind says nothing about what the mail holds. */
  "email",
]);
export const MediaKind = z.enum(["photo", "document", "audio", "video", "sticker", "voice"]);
export const MediaFetchStatus = z.enum(["pending", "fetched", "failed", "expired"]);

/** §2.6's session-window derivation, served computed so no client re-derives
 * 24-hour arithmetic: WhatsApp `open` iff now < last_inbound_at + 24h;
 * Telegram and email threads are always `open` with `closes_at` null (no
 * session window exists on either provider; an email thread is inbound only
 * and refuses every send with 409 CHANNEL_INBOUND_ONLY whatever this says,
 * D-327 ruling 9). */
export const WaWindow = z
  .object({
    open: z.boolean(),
    closes_at: isoDateTime({ offset: true }).nullable(),
  })
  .openapi("MessagingWaWindow");

/** The module-09 context beside the thread (§4.1 right panel) — a SUMMARY
 * from the sales service seam, never a cross-module table read. Null when
 * the thread is unclaimed (§4.2). */
export const ConversationCustomerSummary = z
  .object({
    id: Uuid,
    display_name: z.string(),
    tier: z.string(),
  })
  .openapi("MessagingConversationCustomerSummary");

/** §2.6 + the embedded §2.2 identity (address masked per §7 without the
 * `messaging.address.read` field grant — masking, not omission). */
export const Conversation = z
  .object({
    id: Uuid,
    channel_account_id: Uuid,
    provider: MessagingProvider,
    kind: ConversationKind,
    customer_id: Uuid.nullable(),
    primary_order_id: Uuid.nullable(),
    status: ConversationStatus,
    assigned_to: Uuid.nullable(),
    snoozed_until: isoDateTime({ offset: true }).nullable(),
    last_inbound_at: isoDateTime({ offset: true }).nullable(),
    last_outbound_at: isoDateTime({ offset: true }).nullable(),
    last_message_preview: z.string().nullable(),
    unread_count: z.number().int().min(0),
    closed_at: isoDateTime({ offset: true }).nullable(),
    identity: ChannelIdentity,
    wa_window: WaWindow,
    customer: ConversationCustomerSummary.nullable(),
    created_at: isoDateTime({ offset: true }),
    updated_at: isoDateTime({ offset: true }),
  })
  .openapi("MessagingConversation");

export const ConversationListResponse = z
  .object({ items: z.array(Conversation), next_cursor: z.string().nullable() })
  .openapi("MessagingConversationListResponse");

/** §2.8 item on a thread message. `download_url` is minted per-request
 * post-RLS (§9.12): the file registry's 5-minute GET, served as an
 * attachment, for a registry file (D-322), and a 15-minute GET for a row
 * that still holds its pre-0076 key until the re-home moves it. Null unless
 * `fetch_status='fetched'` (a pending/expired inbound fetch has no object to
 * sign). */
export const MessageMediaItem = z
  .object({
    id: Uuid,
    kind: MediaKind,
    mime_type: z.string().nullable(),
    byte_size: z.number().int().nullable(),
    caption: z.string().nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    fetch_status: MediaFetchStatus,
    download_url: z.string().nullable(),
    file_name: z.string().nullable().openapi({
      description:
        "The file's name as its registry row keeps it: the name the manager attached it with (D-329), or an email part's name as the sender's client gave it (D-327 ruling 14). Null for a chat item that recorded none, and for an item still held by its pre-0076 key, which has no registry row",
    }),
  })
  .openapi("MessagingMessageMediaItem");

/** Why an email part was neither stored nor held (spec 17 §2.14, D-327
 * rulings 7 and 8): the virus verdict was not `PASS`, the spam verdict was
 * `FAIL`, the part was larger than 25 MB, its type is off the purpose's list,
 * its bytes are not its declared type, or its held object expired before the
 * sender was linked. */
export const EmailOmittedPartReason = z.enum([
  "virus_not_clean",
  "spam",
  "too_large",
  "type_not_allowed",
  "bytes_mismatch",
  "expired_before_linking",
]);

/** A part extracted under the inbound bucket's `held/` and not yet in the
 * registry (spec 17 §2.14 `held_parts`): an unknown sender's file, waiting
 * until a person links the sender, or a quarantined mail's. `receipt_id` and
 * `index` address it on the held-part download (§5); the object key is never
 * served. `name` is the part's file name as the sender's client gave it, null
 * when the part carried none. */
export const EmailHeldPart = z
  .object({
    receipt_id: Uuid,
    index: z.number().int().min(0),
    name: z.string().nullable(),
    content_type: z.string(),
    byte_size: z.number().int().min(0),
  })
  .openapi("MessagingEmailHeldPart");

/** A part that was neither stored nor held, with its reason (§2.14
 * `omitted_parts`). */
export const EmailOmittedPart = z
  .object({
    index: z.number().int().min(0),
    name: z.string().nullable(),
    reason: EmailOmittedPartReason,
  })
  .openapi("MessagingEmailOmittedPart");

/** §2.7 as the thread renders it (§4.1 bubbles): status ticks, the failed
 * bubble's `status_detail`, the template badge via `template_code`, media
 * with signed URLs. `provider_message_id`/`template_vars` stay server-side.
 * A message the send worker refused before the provider call (task #278)
 * carries one of the typed details `body_empty`, `body_too_long:{length}`,
 * `body_markup_invalid` or `button_url_invalid:{index}`, which the §4.1
 * bubble says in the reader's language.
 *
 * `body` IS THE FENCED BODY (D-301). For a reader without `sales.read_value`,
 * a template message whose template marks a value (a quote's total, an
 * order's tracking link) arrives with each such value replaced by `•••`, and
 * `value_withheld` is true. A message the server cannot re-render faithfully
 * (its variant edited or gone, or no stored variables) arrives with `body`
 * null and `value_withheld` true. The customer's own words and text a person
 * typed are served as they are. An inbound message the customer FORWARDED is
 * not the customer's words (often it is our own quote or invoice sent back),
 * so for the same reader it arrives with `body` null, no media,
 * `value_withheld` true and `withheld_reason` "forwarded" (D-337 ruling 5);
 * a reply's quoted text never enters a row, and the reply links to the
 * quoted message through `reply_to_message_id`, which carries its own fence.
 * A CREDENTIAL IN A SENT BODY is served to no reader (D-585): the quote's
 * accept link arrives as `•••` for a holder of `sales.read_value` too, with
 * `value_withheld` true and `withheld_reason` "credential", because the
 * link accepts the offer as the customer. A priced document the platform
 * sent with a message (the quote PDF) is left out of `media` for a reader
 * without `sales.read_value`, with `media_withheld` true.
 * `value_withheld` is REQUIRED (D-301 round 7):
 * the server has sent it on every message since it was added, and a response
 * property made required is additive to every client, which oasdiff confirms
 * (`pnpm run ci:contracts`).
 *
 * AN EMAIL MESSAGE (P3-E2 PR E2-2e, D-327 rulings 12, 14 and 18) is inbound,
 * `content_kind` `email`, and carries its subject, its marks and its parts in
 * the keys after `withheld_reason`, which every message carries so a client
 * reads one shape. On a chat message `subject`, `sender_verified`,
 * `email_receipt_id` and `released_by_name` are null, `body_truncated` and
 * `has_quoted_text` are false, and both part lists are empty. `body` is the
 * sender's new text, which is the sender's own words and is served as they
 * wrote it, addresses included. The quoted or forwarded part under it is
 * `quoted_body`, the exception to D-301's rule on people's own words: it is
 * usually our own earlier mail and may hold a price, so it is present only
 * for a holder of `sales.read_value` (null when the mail had none) and absent
 * for every other reader, who reads `has_quoted_text` instead. A holder
 * without `messaging.address.read` reads it with every address in it
 * masked. */
export const ThreadMessage = z
  .object({
    id: Uuid,
    conversation_id: Uuid,
    direction: MessageDirection,
    sender_kind: MessageSenderKind,
    sender_id: Uuid.nullable(),
    body: z.string().nullable(),
    content_kind: MessageContentKind,
    template_code: z.string().nullable(),
    reply_to_message_id: Uuid.nullable(),
    status: MessageStatus,
    status_detail: z.string().nullable(),
    priority: MessagePriority,
    media: z.array(MessageMediaItem),
    provider_ts: isoDateTime({ offset: true }).nullable(),
    sent_at: isoDateTime({ offset: true }).nullable(),
    delivered_at: isoDateTime({ offset: true }).nullable(),
    read_at: isoDateTime({ offset: true }).nullable(),
    failed_at: isoDateTime({ offset: true }).nullable(),
    created_at: isoDateTime({ offset: true }),
    value_withheld: z.boolean().openapi({
      description:
        "D-301: true when `body` is not the stored text, because this reader lacks `sales.read_value` and the message carried a withheld value (masked as `•••`, or the body withheld whole as null), or, since D-337, it is an inbound message the customer forwarded (the body withheld whole as null and its media not served). False otherwise. Present on every message.",
    }),
    withheld_reason: z.enum(["value", "forwarded", "credential"]).optional().openapi({
      description:
        "D-337 ruling 5: why the body was withheld, present only when `value_withheld` is true. `value` is D-301's case, a template message's marked values. `forwarded` is an inbound message the customer forwarded from elsewhere, whose text and media are served only to a reader holding `sales.read_value`, because a forward is often our own quote or invoice sent back. `credential` (D-585) is a reader served every other value, a holder of `sales.read_value`, for whom only a credential in the body was masked: the quote's accept link, which accepts the offer as the customer and is served to no reader of the thread.",
    }),
    media_withheld: z.boolean().openapi({
      description:
        "D-585: true when the message carried files this reader is not served, so `media` is empty and no URL was minted: a priced document the platform sent (the quote PDF) for a reader without `sales.read_value`, or a forward's files (D-337 ruling 5). `value_withheld` speaks of the text alone, so a document-only message withheld from this reader has `media_withheld` true and `value_withheld` false. False on every message that carried no files. Present on every message.",
    }),
    subject: z.string().nullable().openapi({
      description:
        "D-327: an email message's Subject header as decoded, the sender's own words, served to every reader of the thread. Null on a chat message and on a mail that had no subject",
    }),
    body_truncated: z.boolean().openapi({
      description:
        "D-327 ruling 16: true when an email's text was longer than 64 KB and was cut. False on every chat message",
    }),
    sender_verified: z.boolean().nullable().openapi({
      description:
        "D-327 ruling 6: true only when SES's DMARC verdict for the mail was PASS, so the office reads `Sender domain verified`; false for a mail a person released from Unverified mail or an unknown sender's mail that did not pass. Null on every chat message",
    }),
    has_quoted_text: z.boolean().openapi({
      description:
        "D-327 ruling 12: true when the email had a quoted or forwarded part, whether or not this reader is served it, so a reader without `sales.read_value` can be told an earlier message is hidden. False on every chat message",
    }),
    quoted_body: z.string().nullable().optional().openapi({
      description:
        "D-327 ruling 12: the email's quoted or forwarded part, from its first reply or forward separator on. Present on an email message only for a reader holding `sales.read_value`, null when the mail had no such part, and absent for every other reader and on every chat message. A holder without `messaging.address.read` reads every address in it masked",
    }),
    email_receipt_id: Uuid.nullable().openapi({
      description:
        "D-327 ruling 14: the receipt that landed or released this email message, which addresses its held parts on the held-part download. Null on every chat message",
    }),
    released_by_name: z.string().nullable().openapi({
      description:
        "D-327 ruling 18: the name of the person who released this mail from Unverified mail, null unless a person released it",
    }),
    held_parts: z.array(EmailHeldPart).openapi({
      description:
        "D-327 ruling 7: the email's parts waiting under `held/` until a person links the sender, each downloadable by a holder of `messaging.conversation.manage` through the held-part route. Empty once the sender is linked and on every chat message",
    }),
    omitted_parts: z.array(EmailOmittedPart).openapi({
      description:
        "D-327 rulings 7 and 8: the email's parts that were neither stored nor held, each with its reason. Empty on every chat message",
    }),
  })
  .openapi("MessagingThreadMessage");

export const ThreadMessagesResponse = z
  .object({ items: z.array(ThreadMessage), next_cursor: z.string().nullable() })
  .openapi("MessagingThreadMessagesResponse");

/**
 * A free-form body, at most 4096 UTF-16 units as sent: the unit Telegram
 * measures a text in (`telegramLength`, `TELEGRAM_TEXT_MAX`), so a body the
 * contract takes is never refused later as `body_too_long` (D-643). Zod 4
 * counts `.max()` in code points, which would let 2049 to 4096 characters
 * outside the BMP through, so the bound is a refinement that raises zod's own
 * `too_big`, and the published `maxLength` stays 4096.
 */
const THREAD_SEND_BODY_MAX = 4096;
const ThreadSendBody = z
  .string()
  .min(1)
  .superRefine((value, ctx) => {
    if (value.length > THREAD_SEND_BODY_MAX) {
      ctx.addIssue({
        code: "too_big",
        maximum: THREAD_SEND_BODY_MAX,
        origin: "string",
        inclusive: true,
        input: value,
      });
    }
  })
  .openapi({ maxLength: THREAD_SEND_BODY_MAX });

/** §5 office reply — free-text, media, or template, EXACTLY one shape:
 * `template_code` is mutually exclusive with `body`/`media_s3_keys`, and a
 * request carrying none of the three is refused — both refines below, at
 * the contract layer (400 VALIDATION_FAILED, the platform posture; §5
 * amended P3-B part 2 from "422").
 * The template path runs the §3.3.2 locale chain + full policy gates; a WA
 * free-form send outside the 24h window answers 409 WA_WINDOW_CLOSED and
 * the client retries this same endpoint with `template_code` (§4.1's
 * composer swap). */
export const ThreadSendRequest = z
  .object({
    body: ThreadSendBody.optional(),
    media_s3_keys: z.array(z.string().min(1).max(512)).min(1).max(10).optional(),
    template_code: z.string().min(1).max(64).optional(),
    template_vars: z.record(z.string(), z.string().max(1000)).default({}),
    reply_to_message_id: Uuid.optional(),
  })
  .strict()
  .refine(
    (v) => v.body !== undefined || v.media_s3_keys !== undefined || v.template_code !== undefined,
    {
      message: "one of body, media_s3_keys or template_code is required",
    },
  )
  .refine(
    (v) => v.template_code === undefined || (v.body === undefined && v.media_s3_keys === undefined),
    { message: "template_code is mutually exclusive with body and media_s3_keys" },
  )
  .openapi("MessagingThreadSendRequest");

/** §5 PATCH — assign / snooze / close / claim / set primary order. The §2.6
 * CHECK pairs snoozing with its timestamp, transcribed here: `snoozed_until`
 * required iff `status='snoozed'`. The claim arm names a CONTACT
 * (`customer_contact_id`) — §4.2's attach search picks a module-09 contact,
 * and the thread's `customer_id` derives from it (spec 17 §5, amended with
 * this slice). Explicit nulls clear `assigned_to`/`primary_order_id`. */
export const ConversationPatchRequest = z
  .object({
    /** A member's uuid, `null` to unassign, or the literal `"me"` — the P3-0
     * adjudication's self-assign arm, resolved to the caller server-side: the
     * client has no self-id feed (`/v1/auth/tenants` carries memberships, not
     * a subject) and `GET /v1/members` is ga/fa-only, so a sales seat's
     * "Assign to me" needs a spelling that names nobody. */
    assigned_to: z
      .union([Uuid, z.literal("me")])
      .nullable()
      .optional(),
    status: ConversationStatus.optional(),
    snoozed_until: isoDateTime({ offset: true }).optional(),
    customer_contact_id: Uuid.optional(),
    primary_order_id: Uuid.nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: "at least one field is required" })
  .refine((v) => (v.status === "snoozed") === (v.snoozed_until !== undefined), {
    message: "snoozed_until is required exactly when status='snoozed' (§2.6 CHECK)",
  })
  .openapi("MessagingConversationPatchRequest");

export const ConversationReadResponse = z
  .object({ id: Uuid, unread_count: z.literal(0) })
  .openapi("MessagingConversationReadResponse");

/** §5 upload-url — 25 MB cap and the mime allowlist (images, pdf, office
 * docs, audio) are checked HERE and re-checked against the stored object at
 * send persist (§3.3.3): a declared size is a claim, the object's HEAD is
 * the fact. */
export const MediaUploadUrlRequest = z
  .object({
    filename: z.string().min(1).max(255),
    mime_type: z.string().min(1).max(255),
    byte_size: z.number().int().min(1),
  })
  .strict()
  .openapi("MessagingMediaUploadUrlRequest");

export const MediaUploadUrlResponse = z
  .object({
    upload_url: z.string(),
    /** Tenant-prefixed `tenants/{tenant_id}/messaging/uploads/{uuid}/{name}.{ext}`,
     * the name the upload's own, cleaned (D-329), so the customer receives
     * the file under it — the value passed as `media_s3_keys[]` (§5). */
    s3_key: z.string(),
    expires_at: isoDateTime({ offset: true }),
  })
  .openapi("MessagingMediaUploadUrlResponse");

/** §2.9 evidence row (append-only; `messages.status` is the rollup). */
export const MessageDeliveryEvent = z
  .object({
    event: z.enum(["accepted", "sent", "delivered", "read", "failed", "deleted"]),
    provider_error_code: z.string().nullable(),
    provider_error_message: z.string().nullable(),
    provider_ts: isoDateTime({ offset: true }).nullable(),
    created_at: isoDateTime({ offset: true }),
  })
  .openapi("MessagingMessageDeliveryEvent");

export const MessageDetailResponse = z
  .object({
    message: ThreadMessage,
    delivery_events: z.array(MessageDeliveryEvent),
  })
  .openapi("MessagingMessageDetailResponse");

export const MessageRetryResponse = z
  .object({ id: Uuid, status: z.literal("queued") })
  .openapi("MessagingMessageRetryResponse");

/** §4.4's log tail row. `kind` is DERIVED from the raw payload's top-level
 * keys (a typed classification, never prose — D-225); `payload` is present
 * ONLY on `processing_status='error'` rows (§4.4's payload viewer), so the
 * tail stays light and a healthy log never ships raw provider bodies — and
 * only to a reader holding both `sales.read_value` (D-301: a provider update
 * can quote our own outbound words, a total or a tracking link, back inside
 * it) and `messaging.address.read` (#272: every update carries the sender's
 * chat id or phone number in clear, which §7 masks without that grant). */
export const WebhookEventLogItem = z
  .object({
    id: Uuid,
    provider: ChatProvider,
    /** The §2.10 dedup key (Telegram `update_id`; WA: entry sha256) — the one
     * fact that names WHICH provider event a tail row is, so §10.12's
     * "webhook_events shows one row" is assertable on the glass (the §4.4 row
     * renders it as `data-provider-event-id`; §5 row amended P3-B part 2). */
    provider_event_id: z.string(),
    kind: z.enum(["message", "callback_query", "my_chat_member", "other"]),
    processing_status: z.enum(["pending", "processed", "skipped", "error"]),
    signature_valid: z.boolean(),
    error: z.string().nullable(),
    received_at: isoDateTime({ offset: true }),
    processed_at: isoDateTime({ offset: true }).nullable(),
    payload: z.record(z.string(), z.unknown()).optional(),
  })
  .openapi("MessagingWebhookEventLogItem");

export const WebhookEventLogResponse = z
  .object({ items: z.array(WebhookEventLogItem) })
  .openapi("MessagingWebhookEventLogResponse");

// --- email intake (§2.1, §2.14, §3.4's email arm and §5, P3-E2 PR E2-2e) ----

/** GET/POST /v1/messaging/email-intake, the tenant's one monitored address
 * (D-327 ruling 5). `enabled` is true while the tenant's `email` account is
 * `active`. `address` and `account_id` are null until intake is first turned
 * on, and keep their values while it is off, because the address is fixed
 * once minted. The address is the tenant's own and is served in clear to
 * every reader of the card. */
export const EmailIntake = z
  .object({
    enabled: z.boolean(),
    address: z.string().nullable(),
    account_id: Uuid.nullable(),
  })
  .openapi("MessagingEmailIntake");

/** One row of the Unverified mail list (§4.2, D-327 rulings 7 and 18): a
 * `quarantined` receipt. `senders` is the receipt's `from_addresses` in
 * header order, or the From header as decoded when it parsed to no mailbox,
 * each in clear for a holder of `messaging.address.read` and masked for every
 * other reader (§7). `mailbox_count` is the number of mailboxes the From
 * parsed to, which a release's `mailbox_index` counts into: 0 means the mail
 * can only be discarded, and above 1 the releaser must choose. The customer
 * and conversation come from what the worker resolved when it held the mail,
 * and a release resolves the sender again (§3.4's email arm, step 8). */
export const EmailQuarantineItem = z
  .object({
    id: Uuid,
    received_at: isoDateTime({ offset: true }),
    senders: z.array(z.string()),
    mailbox_count: z.number().int().min(0),
    subject: z.string().nullable(),
    customer_id: Uuid.nullable(),
    customer_display_name: z.string().nullable(),
    conversation_id: Uuid.nullable(),
    held_parts: z.array(EmailHeldPart),
  })
  .openapi("MessagingEmailQuarantineItem");

export const EmailQuarantineListResponse = z
  .object({ items: z.array(EmailQuarantineItem), next_cursor: z.string().nullable() })
  .openapi("MessagingEmailQuarantineListResponse");

/** Release names its mailbox by its index into the receipt's
 * `from_addresses`, never by value, so a releaser without the address grant
 * can choose one and the answer is no oracle for an address (D-327 ruling
 * 18). It may be left out when the From parsed to exactly one mailbox. A
 * missing index for several mailboxes, an index that names none, and any
 * release of a mail whose From parsed to no mailbox answer 422
 * EMAIL_RELEASE_MAILBOX_INVALID, which is why the schema sets no upper
 * bound. */
export const EmailQuarantineReleaseRequest = z
  .object({
    mailbox_index: z.number().int().min(0).optional(),
  })
  .strict()
  .openapi("MessagingEmailQuarantineReleaseRequest");

/** The released mail's message and the thread it landed on: the sender's own
 * thread, or a triage thread when the sender is not a known contact. A
 * receipt already released answers the same pair again with no write. */
export const EmailQuarantineReleaseResponse = z
  .object({
    message_id: Uuid,
    conversation_id: Uuid,
  })
  .openapi("MessagingEmailQuarantineReleaseResponse");

export const EmailQuarantineDiscardResponse = z
  .object({})
  .strict()
  .openapi("MessagingEmailQuarantineDiscardResponse");

/** A held part's 5-minute GET on its `held/` object, served as an attachment
 * under a name derived from the declared type (D-327 ruling 7). */
export const EmailHeldPartDownloadResponse = z
  .object({
    url: z.string(),
    expires_at: isoDateTime({ offset: true }),
  })
  .openapi("MessagingEmailHeldPartDownloadResponse");

// --- problem codes (§5; the module-catalog-parity contract) -----------------

/** Module 17 problem `code` values (RFC 9457 problem+json, UPPER_SNAKE —
 * §5). The parity gate holds these against the raise sites both ways. */
export const messagingProblemCodes = [
  /** 422 — connect with provider='whatsapp' while D-232 stands (no BSP, Meta
   * verification pending); the §4.4 card states the parking. */
  "WA_PARKED",
  /** 422 — getMe refused the pasted bot token; the provider's own error text
   * rides the problem extensions verbatim (§4.4 error states). */
  "TG_TOKEN_INVALID",
  /** 409 — a live account for this provider already exists (§2.1 partial
   * unique; one bot / one number per tenant in v1). */
  "ACCOUNT_EXISTS",
  /** 422 — owner_kind/owner_id does not resolve via the owning module
   * (module 09 contact / module 16 user), or names an archived owner. */
  "OWNER_NOT_FOUND",
  /** 403 — the D-040 narrowings: customer-contact link codes / identity
   * revokes / preferences for a principal outside sales+admins, or a
   * non-admin touching an identity that is not their own. */
  "OWNER_SCOPE_FORBIDDEN",
  /** 409 — revoke on an already-revoked identity (no transition out of
   * `revoked`; §3.2 state machine). */
  "IDENTITY_ALREADY_REVOKED",
  /** 409 — template create with a code the tenant already has (§2.4). */
  "TEMPLATE_EXISTS",
  /** 422 — send names a template_code the tenant does not have, or one with
   * no variant at all for the resolved provider (§3.3.2's locale chain found
   * nothing to even evaluate). */
  "TEMPLATE_NOT_FOUND",
  /** 422 — a required template variable is missing/empty at render time
   * (§3.3.3 — refused BEFORE persist). */
  "TEMPLATE_VARS_INVALID",
  /** 422 — PATCH tries to deactivate a system template (§2.4: seeded
   * templates cannot be deleted — or silently starved). */
  "SYSTEM_TEMPLATE_PROTECTED",
  /** 409 — §3.3.1 resolution found NO verified chat identity on ANY provider
   * for the recipient; nothing was persisted. Module 12 (P4) hears this and
   * falls back to email per its own rules. */
  "NO_CHANNEL",
  // --- part 2 (the conversation-inbox slice) --------------------------------
  /** 409 — a WA free-form thread reply outside the 24h session window (§5,
   * M17-S10): the client retries the SAME endpoint with `template_code`
   * (§4.1's composer swap renders the picker for exactly this state). */
  "WA_WINDOW_CLOSED",
  /** 409 — manual retry on a message that is neither `failed_retryable` nor
   * a `failed_permanent` whose failure class was retryable exhaustion (§5's
   * "409 unless" arm — a suppressed or delivered message has nothing to
   * retry). */
  "MESSAGE_NOT_RETRYABLE",
  /** 409 — the §3.4.3 manual-claim arm on a thread whose identity is already
   * `verified` (claim promotes PENDING identities only; re-linking a
   * verified address is the §3.2 code-handshake's business). */
  "CONVERSATION_ALREADY_CLAIMED",
  /** 422 — upload-url or send persist: the media object exceeds the §2.8
   * 25 MB cap, or one message's objects exceed 50 MB together (D-329). */
  "MEDIA_TOO_LARGE",
  /** 422 — send persist: a message with attachments whose text is longer
   * than the 1024 characters a messenger carries with files (D-329). */
  "MEDIA_CAPTION_TOO_LONG",
  /** 422 — upload-url or send persist: mime type outside the §5 allowlist
   * (images, pdf, office docs, audio). */
  "MEDIA_TYPE_UNSUPPORTED",
  /** 422 — a `media_s3_keys[]` entry is not this tenant's messaging-uploads
   * prefix, or no object exists at the key (§3.3.3: keys must be
   * tenant-prefixed; the S3 object already exists via upload-url). */
  "MEDIA_KEY_INVALID",
  // --- email intake (P3-E2 PR E2-2e, D-327, five codes) ---------------------
  /** 409. A send addressed to an email conversation (D-327 ruling 9): the
   * second of §3.3 step 3's thread refusals, after the 404 and before the
   * reply-target 422, NO_CHANNEL and WA_WINDOW_CLOSED, so nothing is checked,
   * copied or persisted first. Replying by email lands with module 12. */
  "CHANNEL_INBOUND_ONLY",
  /** 409. Turning email intake on in a deployment that configures no
   * InboundMailSource (§5 email-intake/enable). */
  "EMAIL_INTAKE_UNAVAILABLE",
  /** 404. An Unverified mail act or a held-part download naming a receipt
   * this tenant does not have, or, on the download, a part the receipt does
   * not hold: an index it never held, a part already moved into the
   * registry, or one whose held object has expired. */
  "EMAIL_RECEIPT_NOT_FOUND",
  /** 409. A release of a discarded receipt or a discard of a released one,
   * which another person has decided, and either act on a receipt that was
   * never held. */
  "EMAIL_RECEIPT_NOT_HELD",
  /** 422. A release whose `mailbox_index` is missing for a From with several
   * mailboxes or names none of them, and every release of a mail whose From
   * parsed to no mailbox, which can only be discarded (D-327 ruling 18). */
  "EMAIL_RELEASE_MAILBOX_INVALID",
] as const;

export type MessagingProblemCode = (typeof messagingProblemCodes)[number];
